import { demoWorkspace, platformServices } from './Values';
import { getYMD } from './analytics';

export const dobStr = (dob) => {
  return (
    dob.substring(5, 7) +
    '/' +
    dob.substring(8, 10) +
    '/' +
    dob.substring(0, 4)
  );
};

export const age = (dob) => {
  const tNow = new Date().getTime();
  return Math.floor((tNow - new Date(dob).getTime()) / 31536000000);
};

export const recentWarnings = (warningLog) => {
  if (warningLog && warningLog.length > 0) {
    return warningLog.reverse().slice(0, 3);
  } else {
    return [];
  }
};

export const activeMeds = (medications) => {
  const ymRef = getYMD(); // current ymRef
  return (medications || []).filter(
    (med) => med.endYM === '' || med.endYM >= ymRef,
  );
};

export const latestMessageDT = (messageData) => {
  messageData = messageData || [];
  if (messageData.length > 0) {
    const lastIdx = messageData.length - 1;
    return messageData[lastIdx].time;
  }
  return '1970-01-01';
};

export const latestMessage = (messageData) => {
  messageData = messageData || [];
  if (messageData.length > 0) {
    const lastIdx = messageData.length - 1;
    return messageData[lastIdx].value;
  }
  return '';
};

export const messageFreq = (messageData) => {
  const tNow = new Date().getTime();
  const d7iso = new Date(tNow - 7 * 86400000).getTime();
  return (messageData || []).filter(
    (message) => new Date(message.time).getTime() > d7iso,
  ).length;
};

export const activeMemos = (noteData) => {
  return (noteData || [])
    .filter(
      (note) =>
        note.noteType &&
        note.noteType === 'memo' &&
        note.isActive &&
        !note.isDeleted,
    )
    .map((note) => ({
      ...note,
      value: parseNote(note.value),
    }));
};

export const numUnreadMessages = (messageData) => {
  return (messageData || []).filter(
    (message) => !message.isRead && !message.msgType,
  ).length;
};

export const isDemoAccountMember = (member) => {
  return member && member.owner_id === demoWorkspace;
}

export const isSuperAdmin = (user) => {
  return user && user.role === "admin" && Boolean(user.isSuperAdmin) === true;
}

export const unparseNote = (note) => {
  if (typeof note !== 'string') {
    return note;
  }

  if (!note) return "";

  return note
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&sol;/g, '/');
};

export const hasCcmService = (member) =>
  (member?.services || []).includes(platformServices.CCM);

export const isActiveCcmMember = (member) =>
  hasCcmService(member) && !member?.disenrolledCcm;

export const hasRpmService = (member) => {
  const services = member?.services;
  if (!services || services.length === 0) {
    return true;
  }
  return services.includes(platformServices.RPM);
};

export const isActiveRpmMember = (member) =>
  hasRpmService(member) && !member?.disenrolled;

export const isActiveRpmAndCcmMember = (member) =>
  isActiveRpmMember(member) && isActiveCcmMember(member);

export const isDisenrolledFromRpm = (member) => member?.disenrolled === true;

export const isDisenrolledFromCcm = (member) => member?.disenrolledCcm === true;

export const disenrolledMembersOrQuery = () => ({
  $or: [{ disenrolled: true }, { disenrolledCcm: true }],
});

export const matchesMemberServiceTab = (
  member,
  serviceTab,
  listFilter,
) => {
  if (listFilter === 'disenrolled') {
    if (serviceTab === 'rpm') {
      return isDisenrolledFromRpm(member);
    }
    if (serviceTab === 'ccm') {
      return isDisenrolledFromCcm(member);
    }
    if (serviceTab === 'rpm-ccm') {
      return isDisenrolledFromRpm(member) && isDisenrolledFromCcm(member);
    }
    return isDisenrolledFromRpm(member) || isDisenrolledFromCcm(member);
  }

  if (serviceTab === 'rpm') {
    return isActiveRpmMember(member);
  }
  if (serviceTab === 'ccm') {
    return isActiveCcmMember(member);
  }
  if (serviceTab === 'rpm-ccm') {
    return isActiveRpmAndCcmMember(member);
  }

  return isActiveRpmMember(member) || isActiveCcmMember(member);
};

export const activeRpmQuery = () => ({
  disenrolled: { $ne: true },
  $or: [
    { services: platformServices.RPM },
    { services: { $exists: false } },
    { services: { $size: 0 } },
  ],
});

export const activeCcmQuery = () => ({
  services: platformServices.CCM,
  disenrolledCcm: { $ne: true },
});

export const activeRpmAndCcmQuery = () => ({
  disenrolled: { $ne: true },
  services: { $all: [platformServices.RPM, platformServices.CCM] },
  disenrolledCcm: { $ne: true },
});

/** Union of active RPM and active CCM (deduped by member). */
export const activeAllMembersQuery = () => ({
  $or: [activeRpmQuery(), activeCcmQuery()],
});

export const getEnrolledServices = (member) => {
  const enrolled = [];
  if (isActiveRpmMember(member)) {
    enrolled.push(platformServices.RPM);
  }
  if (isActiveCcmMember(member)) {
    enrolled.push(platformServices.CCM);
  }
  return enrolled;
};

export const formatEnrolledServices = (member) =>
  getEnrolledServices(member).join(', ');

/** Returns a new services array with any missing entries from servicesToEnsure added. */
export const ensureMemberServices = (services, servicesToEnsure) => {
  const next = [...(services || [])];
  for (const service of servicesToEnsure) {
    if (!next.includes(service)) {
      next.push(service);
    }
  }
  return next;
};

export const parseNote = (note) => {
  if (typeof note !== 'string') {
    return note;
  }

  if (!note) return ""

  return note
    .replace(/<\/?(p|br|i|b|u)\b[^>]*>/gi, "")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\//g, "&sol;");
};


